import { AlertCircle, CheckCircle2, Inbox, LoaderCircle, SearchX, X } from 'lucide-react';
import {
  type ButtonHTMLAttributes,
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useId,
  useRef,
} from 'react';
import { statusLabel, statusTone } from '../lib/format';

export function Button({
  children,
  variant = 'primary',
  loading = false,
  className = '',
  disabled,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  loading?: boolean;
}) {
  return (
    <button
      {...props}
      className={`button button--${variant} ${className}`.trim()}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
    >
      {loading && <LoaderCircle className="button__spinner" aria-hidden="true" size={18} />}
      {children}
    </button>
  );
}

export function StatusPill({ status, label }: { status?: string; label?: string }) {
  const tone = statusTone(status);
  return <span className={`status status--${tone}`}>{label || statusLabel(status)}</span>;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {description && <p className="page-header__description">{description}</p>}
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </header>
  );
}

export function Loader({ label = 'Chargement…', cards = 0 }: { label?: string; cards?: number }) {
  if (cards > 0) {
    return (
      <div className="skeleton-grid" role="status" aria-label={label} aria-busy="true">
        {Array.from({ length: cards }, (_, index) => (
          <div className="skeleton-card" key={index} aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="inline-loader" role="status" aria-live="polite">
      <LoaderCircle className="spinner" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  filtered = false,
}: {
  title: string;
  description: string;
  action?: ReactNode;
  filtered?: boolean;
}) {
  const Icon = filtered ? SearchX : Inbox;
  return (
    <div className="empty-state">
      <span className="empty-state__icon">
        <Icon aria-hidden="true" />
      </span>
      <h2>{title}</h2>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function ErrorState({
  message,
  onRetry,
  title = 'Une erreur est survenue',
}: {
  message: string;
  onRetry?: () => void;
  title?: string;
}) {
  return (
    <div className="empty-state empty-state--error" role="alert">
      <span className="empty-state__icon">
        <AlertCircle aria-hidden="true" />
      </span>
      <h2>{title}</h2>
      <p>{message}</p>
      {onRetry && <Button onClick={onRetry}>Réessayer</Button>}
    </div>
  );
}

export function Notice({
  tone = 'info',
  title,
  children,
}: PropsWithChildren<{ tone?: 'info' | 'success' | 'warning' | 'error'; title?: string }>) {
  return (
    <div className={`notice notice--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      {tone === 'success' ? (
        <CheckCircle2 aria-hidden="true" />
      ) : (
        <AlertCircle aria-hidden="true" />
      )}
      <div>
        {title && <strong>{title}</strong>}
        <div>{children}</div>
      </div>
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  required,
  children,
}: PropsWithChildren<{ label: string; hint?: string; error?: string; required?: boolean }>) {
  const id = useId();
  return (
    <div className={`field ${error ? 'field--error' : ''}`} data-field-id={id}>
      <div className="field__label">
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </div>
      {children}
      {hint && !error && <p className="field__hint">{hint}</p>}
      {error && (
        <p className="field__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function Modal({
  open,
  title,
  description,
  children,
  onClose,
}: PropsWithChildren<{
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
}>) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleCancel = (event: Event) => {
      event.preventDefault();
      onClose();
    };
    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [onClose]);

  return (
    <dialog ref={dialogRef} className="modal" aria-labelledby={titleId} onClose={onClose}>
      <div className="modal__head">
        <div>
          <h2 id={titleId}>{title}</h2>
          {description && <p>{description}</p>}
        </div>
        <button className="icon-button" type="button" aria-label="Fermer" onClick={onClose}>
          <X aria-hidden="true" />
        </button>
      </div>
      {children}
    </dialog>
  );
}

export function ResultDialog({
  open,
  tone,
  title,
  message,
  actionLabel = 'Continuer',
  onClose,
}: {
  open: boolean;
  tone: 'success' | 'error';
  title: string;
  message?: string;
  actionLabel?: string;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleCancel = (event: Event) => {
      event.preventDefault();
      onClose();
    };
    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [onClose]);

  return (
    <dialog
      ref={dialogRef}
      className={`result-dialog result-dialog--${tone}`}
      onClose={onClose}
      aria-live="assertive"
    >
      {open && (
        <div className="result-dialog__body">
          <span className="result-dialog__icon" aria-hidden="true">
            <svg viewBox="0 0 52 52" role="presentation">
              <circle className="result-dialog__ring" cx="26" cy="26" r="24" fill="none" />
              {tone === 'success' ? (
                <path
                  className="result-dialog__mark"
                  fill="none"
                  d="M15 27.5 L23 35 L38 18"
                />
              ) : (
                <path
                  className="result-dialog__mark"
                  fill="none"
                  d="M18 18 L34 34 M34 18 L18 34"
                />
              )}
            </svg>
          </span>
          <h2>{title}</h2>
          {message && <p>{message}</p>}
          <button className="button button--primary" type="button" onClick={onClose} autoFocus>
            {actionLabel}
          </button>
        </div>
      )}
    </dialog>
  );
}

export function Pagination({
  page,
  hasNext,
  onPage,
}: {
  page: number;
  hasNext: boolean;
  onPage: (page: number) => void;
}) {
  return (
    <nav className="pagination" aria-label="Pagination">
      <Button variant="secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        Précédent
      </Button>
      <span>Page {page}</span>
      <Button variant="secondary" disabled={!hasNext} onClick={() => onPage(page + 1)}>
        Suivant
      </Button>
    </nav>
  );
}
